import { isEqual } from 'lodash'
import { BIDS_COUNT_ACTION_TYPES } from 'utils/actions/bids'
import {
  SHIPMENT_ACTION_TYPES,
  SHIPMENT_UPDATE_ACTION_TYPES,
  UPDATE_LIST_FROM_UPDATE_REQUEST,
  SHOW_LOADING_STATE_ON_SHIPMENT,
  HIDE_LOADING_ON_ALL_SHIPMENTS,
  SHIPMENT_POD_UPLOADED,
} from 'utils/actions/shipments'
import { setComplianceStatus } from 'utils/carriers'
import { processEquipmentAttributes } from 'utils/shipmentHelpers'

const {
  REQUEST_SHIPMENT_UPDATE,
  SHIPMENT_UPDATE_FAILURE,
  SHIPMENT_UPDATE_SUCCESS,
} = SHIPMENT_UPDATE_ACTION_TYPES

const { BIDS_COUNT_SUCCESS } = BIDS_COUNT_ACTION_TYPES

const SIDEBAR_REQUESTED = 'SIDEBAR_REQUESTED'

export const makeShipmentsReducer = (requestActions = Object.keys(SHIPMENT_ACTION_TYPES)) => {
  const REQUEST_SHIPMENTS = requestActions.find(entry => !entry.match(/(FAILURE|SUCCESS)$/i))
  const SHIPMENTS_SUCCESS = requestActions.find(entry => entry.match(/SUCCESS$/i))
  const SHIPMENTS_FAILURE = requestActions.find(entry => entry.match(/FAILURE$/i))

  const defaultState = {
    firstLoad: true,
    loading: false,
    isFetching: null,
    params: {
      filters: {
        podIds: [],
        includes: ['open_issues'],
      },
      sortField: 'pickups',
      sortOrder: 'asc',
    },
    filterOptions: { amGroups: [] },
    hasNextPage: null,
    error: null,
    shipmentList: [],
    shipmentDetails: null,
  }

  return (state = defaultState, action = {}) => {
    switch (action.type) {
      case REQUEST_SHIPMENTS: {
        const {
          payload: {
            requestData: { params },
          },
        } = action
        return {
          ...state,
          isFetching: true,
          params,
        }
      }
      case SHIPMENTS_SUCCESS: {
        const { shipments = [] } = action.payload || {}
        const {
          payload: {
            filterOptions,
            nextPage,
            requestData: { params },
          },
        } = action
        const { filters } = params || {}
        const { status } = filters || {}

        // To prevent a possible race condition where the user switches statuses,
        // only updates if the status matches the one we are currently fetching
        const { params: currentStateParams } = state
        const { filters: currentStateFilters } = currentStateParams || {}
        const { status: currentStatusFilters } = currentStateFilters || {}

        if (isEqual(currentStatusFilters, status)) {
          return {
            ...state,
            shipmentList: shipments.map(shipment => {
              processEquipmentAttributes(shipment)
              return shipment.coveredBid && shipment.coveredBid.complianceReview
                ? setComplianceStatus(shipment)
                : shipment
            }),
            isFetching: false,
            firstLoad: false,
            error: null,
            filterOptions,
            hasNextPage: nextPage,
            params,
          }
        }
        return state
      }
      case SHIPMENTS_FAILURE: {
        const {
          error: fetchError,
          payload: { error, nextPage },
        } = action
        return {
          ...state,
          isFetching: false,
          firstLoad: false,
          error: error || fetchError,
          hasNextPage: nextPage,
        }
      }
      case REQUEST_SHIPMENT_UPDATE: {
        return {
          ...state,
          loading: true,
          error: null,
          shipmentDetails: null,
        }
      }
      case SHIPMENT_UPDATE_SUCCESS: {
        const { shipment } = action.payload || {}
        return {
          ...state,
          loading: false,
          error: null,
          shipmentDetails: shipment,
        }
      }
      case SIDEBAR_REQUESTED: {
        return {
          ...state,
          shipmentDetails: null,
          error: null,
        }
      }
      case UPDATE_LIST_FROM_UPDATE_REQUEST: {
        const { shipment } = action.payload || {}
        return {
          ...state,
          error: null,
          shipmentList: state.shipmentList.map(s => {
            if (s.id === shipment.id) {
              return {
                ...s,
                info: { ...shipment.info },
                status: { ...shipment.status },
                users: {
                  ...s.users,
                  driverName: shipment.info.driverName,
                  driverId: shipment.info.driverId,
                },
              }
            }
            return s
          }),
        }
      }
      case SHIPMENT_POD_UPLOADED: {
        return {
          ...state,
          shipmentList: state.shipmentList.map(s => {
            if (s.id === action.payload) {
              return { ...s, podUploaded: true }
            }
            return s
          }),
        }
      }
      case SHOW_LOADING_STATE_ON_SHIPMENT: {
        return {
          ...state,
          error: null,
          shipmentList: state.shipmentList.map(s => {
            if (s.id === action.payload) {
              return { ...s, isLoading: true }
            }
            return s
          }),
        }
      }
      case HIDE_LOADING_ON_ALL_SHIPMENTS: {
        return {
          ...state,
          error: null,
          shipmentList: state.shipmentList.map(s => ({ ...s, isLoading: false })),
        }
      }
      case SHIPMENT_UPDATE_FAILURE: {
        const { error } = action
        return {
          ...state,
          loading: false,
          error,
        }
      }
      case BIDS_COUNT_SUCCESS: {
        const { response } = action.payload
        const bids = response?.map(bid => ({ shipmentId: bid.shipmentId, count: bid.count }))
        const bidIds = bids?.map(bid => bid.shipmentId)
        return {
          ...state,
          shipmentList: state.shipmentList?.map(shipment =>
            bidIds?.includes(shipment.id)
              ? {
                  ...shipment,
                  bidCount: bids?.find(bid => bid.shipmentId === shipment.id).count,
                }
              : shipment
          ),
        }
      }
      default:
        return state
    }
  }
}

const shipmentsReducer = makeShipmentsReducer()
export default shipmentsReducer
